import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '../../../../components/shared/icon/icon.component';
import { TablePaginationComponent } from '@app/components/table-pagination/table-pagination.component';
import { InventoryTableSearchComponent } from '@app/pages/inventory/components/inventory-table-functions/inventory-table-search/inventory-table-search.component';
import { CreateStaffDialogComponent } from '../create-staff-dialog/create-staff-dialog.component';
import { StaffEditFormComponent } from '../staff-edit-form/staff-edit-form.component';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { StaffService } from '@app/pages/staff/services/staff.service';
import { IconName } from '@app/utils/enums/icon-enum';
import { StaffColumnKey } from '@app/utils/enums/staff-table-columns.enum';
import { StaffStatus } from '@app/utils/enums/staff';
import {
  FAKE_STAFF_QUANTITY,
  STAFF_TABLE_COLUMNS_CONFIG,
} from '@app/utils/constants/staff.constants';
import { TABLE_PAGINATION_DEFAULT_CONFIG } from '@app/utils/constants/table-pagination.constants';
import {
  Staff,
  StaffStatusConfig,
  StaffPaginatedResponse,
  StaffData,
} from '@app/utils/interfaces/staff.interface';
import { TablePaginationConfig } from '@app/utils/interfaces/table-pagination.interface';

@Component({
  selector: 'app-staff-table',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatMenuModule,
    IconComponent,
    InventoryTableSearchComponent,
    TablePaginationComponent,
  ],
  templateUrl: './staff-table.component.html',
  styleUrl: './staff-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaffTableComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly staffService = inject(StaffService);

  public readonly iconName = IconName;
  public readonly staffColumnKey = StaffColumnKey;

  public readonly staff = signal<Staff[]>([]);
  public readonly staffLoading = signal(false);
  public readonly currentUserId = signal<string>('');
  public readonly staffTableColumnsConfig = signal(STAFF_TABLE_COLUMNS_CONFIG);
  public readonly staffPagination = signal<TablePaginationConfig>(TABLE_PAGINATION_DEFAULT_CONFIG);
  public readonly staffTableColumnKeys = STAFF_TABLE_COLUMNS_CONFIG.map((column) => column.key);

  public readonly skeletonRows = signal<Partial<Staff>[]>(
    Array.from({ length: FAKE_STAFF_QUANTITY }, () => ({})),
  );

  public readonly viewState = computed(() => {
    if (this.staffLoading()) {
      return 'loading';
    }

    if (this.staff().length === 0) {
      return 'empty';
    }

    return 'data';
  });

  public readonly showPagination = computed(() => this.staffPagination().totalItems > 0);

  private readonly defaultStatusConfig: StaffStatusConfig = {
    class: 'offline',
    label: 'staff.table.status.unknown',
  };

  private readonly statusConfig: Record<StaffStatus, StaffStatusConfig> = {
    [StaffStatus.Online]: {
      class: 'online',
      label: 'staff.table.status.online',
    },
    [StaffStatus.Offline]: {
      class: 'offline',
      label: 'staff.table.status.offline',
    },
    [StaffStatus.CreatePassword]: {
      class: 'createPassword',
      label: 'staff.table.status.createPassword',
    },
    [StaffStatus.Deactivated]: {
      class: 'deactivated',
      label: 'staff.table.status.deactivated',
    },
  };

  public ngOnInit(): void {
    this.fetchData();

    this.staffService
      .getPersonalInformation()
      .pipe(
        tap((res) => {
          this.currentUserId.set(res.data.id);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public getStatusConfig(status: string | null | undefined): StaffStatusConfig {
    if (!status) {
      return this.defaultStatusConfig;
    }

    return this.statusConfig[status as StaffStatus] ?? this.defaultStatusConfig;
  }

  public openCreateStaffDialog(): void {
    const dialogRef = this.dialog.open(CreateStaffDialogComponent, {
      width: '808px',
      maxWidth: '95vw',
      panelClass: ['no-radius-dialog'],
      disableClose: true,
    });

    dialogRef.afterClosed().pipe(
      tap((res)=> {
        if(res) {
          this.fetchData()
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe()
  }

  public openEditStaffDialog(staff: StaffData, menuTrigger: MatMenuTrigger): void {
    menuTrigger.closeMenu();

    const dialogRef = this.dialog.open(StaffEditFormComponent, {
      width: '600px',
      data: {
        staff,
        activeUserId: this.currentUserId(),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === true) {
          this.fetchData();
        }
      });
  }

  public handlePageChange(page: number): void {
    this.updatePagination({ currentPage: page });
  }

  public handlePageSizeChange(pageSize: number): void {
    this.updatePagination({ pageSize, currentPage: 1 });
  }

  private fetchData(): void {
    this.staffLoading.set(true);

    const { currentPage, pageSize } = this.staffPagination();

    this.staffService
      .getStaff({
        pageNumber: currentPage,
        pageSize,
      })
      .pipe(
        finalize(() => this.staffLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response: StaffPaginatedResponse) => {
          this.staff.set(response.data.items);
          this.staffPagination.update((pagination) => ({
            ...pagination,
            totalItems: response.data.totalAmount,
          }));
        },
        error: () => {
          this.staff.set([]);
          this.handleOpenErrorModal();
        },
      });
  }

  private updatePagination(paginationConfig: Partial<TablePaginationConfig>): void {
    this.staffPagination.update((previousPagination) => ({
      ...previousPagination,
      ...paginationConfig,
    }));

    this.fetchData();
  }

  private handleOpenErrorModal(): void {
    this.dialog.open(StatusModalComponent, {
      data: {
        success: false,
        title: 'staff.table.errors.title',
        description: 'staff.table.errors.staffLoadFail',
      },
    });
  }
}
